import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { formatSpan } from '@et/schema/date';
import type { Locale } from '@et/schema';
import { EvidenceGlyph } from '../lexicon/EvidenceGlyph.js';
import { witnessFamily, WITNESS_LANES } from '../lexicon/witness.js';
import { clusterPins, densityBuckets, layoutPins, visibleInRange } from './layout.js';
import type { TimeScale } from './scale.js';
import type { TimelineEra, TimelinePin } from './types.js';

/**
 * A vertical passa a dizer alguma coisa.
 *
 * Antes, a fila em que uma peça caía era o resultado de um algoritmo de
 * colisões: não significava nada, e por isso o gráfico lia-se como um monte de
 * marcas a flutuar. Agora cada faixa é uma **família de testemunho** — objectos
 * que sobreviveram, o texto e as suas cópias, quem escreveu sobre o assunto, o
 * que uma administração registou. Dentro da faixa continua a haver colisões
 * resolvidas por sub-fila, mas essa parte é ruído honesto: é a densidade real
 * daquele período.
 *
 * O que isto compra: a mesma cor que está nos filtros e na lista está agora
 * também numa posição, e "os manuscritos começam todos depois do ano 150"
 * passa a ser uma coisa que se vê em vez de uma coisa que se conta.
 */
const SUB_ROWS = 3;
const ROW_HEIGHT = 28;
const LANE_PAD = 10;
const LABEL_WIDTH = 78;
/**
 * A régua encolheu, e passou a estar encostada em baixo.
 *
 * Oitenta e oito pixéis eram o dobro do que os anos precisam: sobrava faixa
 * escura, e a barra ficava a meio do cartão com espaço morto por baixo. O que
 * ela tem de conter são os anos, as marcas de densidade e os losangos da
 * descoberta — cabe tudo em sessenta e dois.
 */
const BAR_HEIGHT = 62;
/**
 * A margem por cima da primeira faixa.
 *
 * A placa do rótulo desenha-se 52px acima da marca. Na primeira sub-fila da
 * primeira faixa isso caía fora do `viewBox` e o rótulo aparecia cortado por
 * cima dos controlos — que é o que se via ao escolher a peça mais antiga.
 */
const TOP_PAD = 58;
/** A goteira à esquerda, fora do domínio da escala, para os nomes das faixas. */
const GUTTER = 92;
const LANE_OFFSET = 20;
const CLUSTER_THRESHOLD = 200;
const CLUSTER_DISTANCE = 14;

/**
 * O SVG não quebra linha nem corta com reticências. Sem isto, um título longo
 * atravessa a placa e entra pela do vizinho, e leem-se dois títulos como um.
 */
const TITLE_CHARS = 21;
const ellipsize = (text: string): string =>
  (text.length <= TITLE_CHARS ? text : `${text.slice(0, TITLE_CHARS - 1).trimEnd()}…`);

const LANES = WITNESS_LANES;

export interface TimelineCanvasProps {
  readonly pins: readonly TimelinePin[];
  readonly scale: TimeScale;
  readonly width: number;
  /** Sub-filas dentro de cada faixa. As faixas em si vêm das famílias. */
  readonly rows?: number;
  readonly locale?: Locale;
  readonly selectedId?: string | null;
  readonly showAlternativeDatings?: boolean;
  readonly onSelect?: (id: string | null) => void;
  readonly onPan?: (deltaPx: number) => void;
  readonly onZoom?: (factor: number, focusX: number) => void;
  readonly eras?: readonly TimelineEra[];
  readonly listId?: string;
  readonly className?: string;
}

interface Resolved {
  readonly pin: TimelinePin;
  readonly x: number;
  readonly width: number;
  readonly discoveryX: number | null;
}

/**
 * §6, §22 — a peça central.
 *
 * Duas decisões governam tudo: **a incerteza é largura** (ponto para data
 * conhecida, barra para intervalo, tracejado para contestada) e **o eixo
 * parte-se**, com a curva objecto → descoberta a atravessar a quebra.
 */
export function TimelineCanvas({
  pins, scale, width, rows = SUB_ROWS, locale = 'en', selectedId = null,
  showAlternativeDatings = false, onSelect, onPan, onZoom, eras = [], listId, className,
}: TimelineCanvasProps) {
  const { t } = useTranslation();
  const svgRef = useRef<SVGSVGElement>(null);
  const dragRef = useRef<{ x: number } | null>(null);
  const [focusIndex, setFocusIndex] = useState(0);
  const [cursorX, setCursorX] = useState<number | null>(null);

  const subRows = Math.max(1, rows);

  const resolved = useMemo<Resolved[]>(() => pins.flatMap((pin) => {
    const x0 = scale.toX(pin.event[0]);
    if (x0 === null) return [];
    const x1 = scale.toX(pin.event[1]) ?? x0;
    const discovery = pin.discovery == null ? null : scale.toX(pin.discovery[0]);
    return [{ pin, x: x0, width: Math.max(0, x1 - x0), discoveryX: discovery }];
  }), [pins, scale]);

  /**
   * As faixas vêm de **todos** os pins e não só dos visíveis: caso contrário
   * o gráfico mudava de altura durante o arrasto, que é a forma mais rápida
   * de tornar um eixo impossível de seguir.
   */
  const lanes = useMemo(() => {
    const present = new Set(resolved.map((r) => witnessFamily(r.pin.evidenceType)));
    const shown = LANES.filter((l) => present.has(l.family));
    return shown.length === 0 ? [LANES[LANES.length - 1]!] : shown;
  }, [resolved]);

  // As alturas das faixas vêm do que cada uma usa — ver `geometry`, abaixo.

  const visible = useMemo(
    () => visibleInRange(resolved, [0, width], 240), [resolved, width]);

  const clusters = useMemo(
    () => clusterPins(visible.items, CLUSTER_DISTANCE, CLUSTER_THRESHOLD),
    [visible.items]);

  const clustered = clusters.filter((c) => c.items.length > 1);
  const singles = clusters.filter((c) => c.items.length === 1).map((c) => c.items[0]!);

  /**
   * A colisão resolve-se **dentro** da faixa. Correr o algoritmo sobre o
   * conjunto todo voltaria a espalhar as peças por filas sem sentido.
   */
  /**
   * Cada faixa fica com a altura que **usa**, e não com a altura máxima.
   *
   * Reservar três sub-filas a toda a gente deixava faixas inteiras com dois
   * terços de vazio — os Registos têm duas peças e ocupavam o mesmo que os
   * Objectos, que têm catorze. A altura passa a ser uma consequência da
   * densidade, que é o que a linha do tempo tem para dizer.
   */
  const { placed, laneTops, barTop, height } = useMemo(() => {
    const perLane = lanes.map((lane) => {
      const mine = singles.filter((r) => witnessFamily(r.pin.evidenceType) === lane.family);
      /*
        Sem prioridade para a peça escolhida.

        Servia para a trazer para a fila de cima quando os rótulos estavam
        todos visíveis e a de cima era a mais legível. Agora que só aparece o
        rótulo da peça apontada, isso deixou de comprar nada — e custava caro:
        reordenar o empacotamento mudava quantas sub-filas cada faixa usa, logo
        a altura do desenho, logo o `ResizeObserver`, logo a escala. Escolher um
        ponto redesenhava o gráfico debaixo do cursor.
      */
      return layoutPins(
        mine.map((r) => ({ id: r.pin.id, x: r.x, width: r.width, labelWidth: LABEL_WIDTH })),
        { rows: subRows, bounds: [4, width - 4] },
      );
    });

    const tops: number[] = [];
    let cursor = 0;
    perLane.forEach((rows) => {
      tops.push(cursor);
      const used = rows.reduce((n, p) => Math.max(n, p.row + 1), 1);
      cursor += used * ROW_HEIGHT + LANE_PAD;
    });

    const items = perLane.flatMap((rows, laneIdx) =>
      rows.map((p) => ({ ...p, lane: laneIdx })));

    return {
      placed: items.sort((a, b) => a.x - b.x || a.id.localeCompare(b.id)),
      laneTops: tops,
      barTop: cursor,
      /*
        Sem os 44px que sobravam por baixo da barra.

        Vinham do desenho original, em que os anos eram escritos **debaixo** da
        régua. Passaram para dentro dela há muito, e a folga ficou — quarenta e
        quatro pixéis de nada entre a régua e o rodapé do cartão, em todos os
        ecrãs, sem nada lá desenhado.
      */
      height: cursor + BAR_HEIGHT + 6,
    };
  }, [singles, lanes, subRows, width]);

  const laneHeightOf = (i: number) =>
    (i + 1 < laneTops.length ? laneTops[i + 1]! : barTop) - laneTops[i]!;

  const byId = useMemo(() => new Map(singles.map((r) => [r.pin.id, r])), [singles]);
  const chronological = placed.map((p) => p.id);
  const density = useMemo(
    () => densityBuckets(resolved.map((r) => r.x), [0, width], 5), [resolved, width]);
  /**
   * O numero de anos na regua vem da largura, e nao de uma constante.
   *
   * Nove marcas cabiam nos 1200px que o desenho tinha quando a largura era
   * fixa. Medido ao contentor, o mesmo nove escrevia "801 a.C.601 a.C.401 a.C."
   * — nao e uma regua, e uma cadeia de caracteres. Cento e dez pixeis e o que
   * um ano com era ocupa em mono, com folga para respirar.
   */
  const ticks = useMemo(() => {
    const all = scale.ticks(Math.max(3, Math.floor(width / 110)));
    // A faixa da descoberta e estreita e recebe a sua quota de marcas na mesma,
    // o que escrevia "1900 d.C.2000 d.C." em cinquenta pixeis. Descartar a
    // marca que nao tem espaco e mais honesto do que a desenhar por cima da
    // anterior: a que fica continua no sitio certo.
    const kept: typeof all = [];
    for (const tick of all) {
      const last = kept[kept.length - 1];
      if (last === undefined || Math.abs(tick.x - last.x) >= 92) kept.push(tick);
    }
    return kept;
  }, [scale, width]);

  const yOf = (p: { lane: number; row: number }) =>
    (laneTops[p.lane] ?? 0) + LANE_OFFSET + p.row * ROW_HEIGHT;

  const selected = selectedId === null ? null : byId.get(selectedId) ?? null;
  const relatedSet = new Set(selected?.pin.relatedIds ?? []);
  const dimmed = selected !== null;
  const cursorYear = cursorX === null ? null : scale.fromX(cursorX);

  const onKeyDown = (e: KeyboardEvent<SVGSVGElement>) => {
    if (chronological.length === 0) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const next = e.key === 'ArrowRight'
        ? Math.min(focusIndex + 1, chronological.length - 1)
        : Math.max(focusIndex - 1, 0);
      setFocusIndex(next);
      const id = chronological[next];
      if (id !== undefined) svgRef.current?.querySelector<SVGGElement>(`[data-pin="${id}"]`)?.focus();
      return;
    }
    if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      const next = e.key === 'Home' ? 0 : chronological.length - 1;
      setFocusIndex(next);
      const id = chronological[next];
      if (id !== undefined) svgRef.current?.querySelector<SVGGElement>(`[data-pin="${id}"]`)?.focus();
      return;
    }
    if (e.key === 'Escape') onSelect?.(null);
  };

  return (
    <svg
      ref={svgRef}
      className={clsx('et-tl', className)}
      viewBox={`${-GUTTER} ${-TOP_PAD} ${width + GUTTER} ${height + TOP_PAD}`}
      width="100%"
      height={height + TOP_PAD}
      role="application"
      aria-label={t('timeline.label')}
      aria-controls={listId}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onWheel={(e) => {
        if (onZoom === undefined) return;
        const rect = svgRef.current?.getBoundingClientRect();
        const x = rect === undefined ? width / 2 : e.clientX - rect.left;
        onZoom(e.deltaY > 0 ? 1.15 : 0.87, x);
      }}
      onPointerDown={(e) => { dragRef.current = { x: e.clientX }; }}
      onPointerMove={(e) => {
        const rect = svgRef.current?.getBoundingClientRect();
        if (rect !== undefined && rect.width > 0) {
          // De pixel de ecrã para unidade do viewBox, que inclui a goteira.
          setCursorX(((e.clientX - rect.left) / rect.width) * (width + GUTTER) - GUTTER);
        }
        if (dragRef.current === null || onPan === undefined) return;
        onPan(e.clientX - dragRef.current.x);
        dragRef.current = { x: e.clientX };
      }}
      onPointerUp={() => { dragRef.current = null; }}
      onPointerLeave={() => { dragRef.current = null; setCursorX(null); }}
    >
      {/* as faixas: uma por família de testemunho, com o nome na goteira */}
      <g className="et-tl__lanes" aria-hidden="true">
        {lanes.map((lane, i) => {
          const top = laneTops[i] ?? 0;
          const h = laneHeightOf(i);
          return (
            <g key={lane.family} className="et-tl__lane" data-family={lane.family}>
              <rect
                x={-GUTTER} y={top} width={width + GUTTER} height={h}
                className="et-tl__laneBand"
              />
              <line x1={-GUTTER} y1={top} x2={width} y2={top} className="et-tl__laneRule" />
              <rect x={-GUTTER} y={top + 5} width={3} height={Math.max(6, h - 10)} className="et-tl__laneMark" />
              <text x={-GUTTER + 12} y={top + 18} className="et-tl__laneName">
                {t(`timeline.lane.${lane.key}`)}
              </text>
            </g>
          );
        })}
      </g>

      {/* o período de cada figura fundadora, por cima das faixas e por baixo das peças */}
      <g className="et-tl__eras" aria-hidden="true">
        {eras.map((era) => {
          const x0 = scale.toX(era.from);
          const x1 = scale.toX(era.to);
          if (x0 === null || x1 === null) return null;
          return (
            <g key={era.id} className="et-tl__era" data-era={era.id}>
              <rect x={x0} y={-TOP_PAD} width={Math.max(2, x1 - x0)} height={barTop + TOP_PAD} />
              <line x1={x0} y1={-TOP_PAD + 20} x2={x0} y2={barTop + BAR_HEIGHT - 22} />
              <line x1={x1} y1={-TOP_PAD + 20} x2={x1} y2={barTop + BAR_HEIGHT - 22} />
              <text x={x1 + 6} y={-TOP_PAD + 16}>{era.label}</text>
            </g>
          );
        })}
      </g>

      {/*
        A régua sob o cursor.

        A queixa era "não percebo o que estou a ver", e metade disso era não
        conseguir dizer em que ano cai uma marca sem seguir uma haste com o
        dedo. A linha e o ano lêem-se sem carregar em nada.
      */}
      {cursorX !== null && cursorX >= 0 && cursorX <= width && (
        <g className="et-tl__cursor" aria-hidden="true">
          <line x1={cursorX} y1={-TOP_PAD} x2={cursorX} y2={barTop + BAR_HEIGHT - 22} />
          {cursorYear !== null && (
            <>
              <rect x={cursorX - 34} y={barTop + BAR_HEIGHT - 21} width={68} height={18} rx={5} />
              <text x={cursorX} y={barTop + BAR_HEIGHT - 8} textAnchor="middle">
                {formatSpan({ earliest: Math.round(cursorYear), latest: Math.round(cursorYear) }, locale)}
              </text>
            </>
          )}
        </g>
      )}

      {/* arcos da selecção: claim, objecção e a data de descoberta */}
      {selected !== null && (
        <g className="et-tl__arcs" aria-hidden="true">
          {[...relatedSet].map((id) => {
            const other = byId.get(id);
            const p = placed.find((x) => x.id === id);
            const from = placed.find((x) => x.id === selected.pin.id);
            if (other === undefined || p === undefined || from === undefined) return null;
            const y1 = yOf(from);
            const y2 = yOf(p);
            return (
              <path key={id} className="et-tl__arc"
                d={`M ${selected.x} ${y1} C ${selected.x} ${(y1 + y2) / 2} ${other.x} ${(y1 + y2) / 2} ${other.x} ${y2}`} />
            );
          })}
          {/*
            O objecto e a sua descoberta, ligados por cima da quebra do eixo.
            São dois mil anos de distância e dois lados do gráfico: sem a curva,
            o losango do lado direito é uma marca sem dono.
          */}
          {selected.discoveryX !== null && (() => {
            const from = placed.find((x) => x.id === selected.pin.id);
            const y0 = from === undefined ? LANE_OFFSET : yOf(from);
            const mid = barTop - 26;
            return (
              <path className="et-tl__arc et-tl__arc--discovery"
                d={`M ${selected.x} ${y0}`
                  + ` C ${selected.x} ${mid} ${selected.discoveryX} ${mid} ${selected.discoveryX} ${barTop + 24}`} />
            );
          })()}
        </g>
      )}

      {/* pins */}
      {placed.map((p, index) => {
        const r = byId.get(p.id);
        if (r === undefined) return null;
        const { pin } = r;
        const y = yOf(p);
        const isSelected = pin.id === selectedId;
        const faded = dimmed && !isSelected && !relatedSet.has(pin.id);
        const span = { earliest: pin.event[0], latest: pin.event[1] };
        const dateText = (locale === 'en' ? pin.dateLabel : undefined) ?? formatSpan(span, locale);

        return (
          <g
            key={pin.id}
            data-pin={pin.id}
            data-category={pin.category}
            data-selected={isSelected ? 'true' : undefined}
            data-deviated={p.deviated ? 'true' : undefined}
            data-disputed={pin.disputed ? 'true' : undefined}
            data-family={witnessFamily(pin.evidenceType)}
            className={clsx('et-tl__pin', `et-tl__pin--${pin.category}`, faded && 'et-tl__pin--faded')}
            role="button"
            tabIndex={index === focusIndex ? 0 : -1}
            aria-label={`${pin.title}, ${dateText}`}
            aria-pressed={isSelected}
            onFocus={() => setFocusIndex(index)}
            onClick={() => onSelect?.(isSelected ? null : pin.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onSelect?.(isSelected ? null : pin.id);
              }
            }}
          >
            {/*
              O SVG só acerta no que está pintado, e um pin pintado são um
              ponto de 6px e duas linhas de texto escondidas. Sem alvo próprio,
              carregar ao lado da marca — que é o que uma pessoa faz — não faz
              nada. São dois rectângulos, e nenhum sobe até à sub-fila de cima.
            */}
            <rect className="et-tl__hit" x={p.labelX - 46} y={y - 26} width={92} height={26} />
            <rect
              className="et-tl__hit"
              x={r.x - 12} y={y - 12}
              width={Math.max(24, r.width + 24)} height={24}
            />
            {p.deviated && <line className="et-tl__trace" x1={r.x} y1={y} x2={p.labelX} y2={y} />}

            {/* incerteza é largura: ponto, barra, ou barra tracejada */}
            {r.width > 1
              ? <rect className="et-tl__span" x={r.x} y={y - 5} width={r.width} height={10} rx={5} />
              : <circle className="et-tl__dot" cx={r.x} cy={y} r={isSelected ? 10 : 7.5} />}

            {showAlternativeDatings && pin.alternativeDatings?.map((alt, i) => {
              const ax = scale.toX(alt[0]);
              if (ax === null) return null;
              const aw = Math.max(2, (scale.toX(alt[1]) ?? ax) - ax);
              return <rect key={i} className="et-tl__ghost" x={ax} y={y - 4} width={aw} height={8} rx={4} />;
            })}

            <rect className="et-tl__chip" x={p.labelX - 74} y={y - 52} width={148} height={40} rx={10} />
            <text className="et-tl__title" x={p.labelX} y={y - 30} textAnchor="middle">
              {ellipsize(pin.title)}
              {/* O título inteiro fica na árvore, para o leitor de ecrã e para o teste. */}
              <title>{pin.title}</title>
            </text>
            <text className="et-tl__date" x={p.labelX} y={y - 17} textAnchor="middle">{dateText}</text>
            {pin.evidenceType !== undefined && (
              <g transform={`translate(${p.labelX - 8} ${y - 48})`}>
                <EvidenceGlyph type={pin.evidenceType} size={16} decorative />
              </g>
            )}
          </g>
        );
      })}

      {/* clusters: nunca escondemos itens em silêncio */}
      {clustered.map((c, i) => (
        <g key={`c${i}`} className="et-tl__cluster" data-testid="cluster"
          role="button" tabIndex={-1}
          aria-label={t('timeline.cluster', { count: c.items.length })}>
          <circle cx={c.x} cy={barTop - 20} r={11} />
          <text x={c.x} y={barTop - 16} textAnchor="middle">{c.items.length}</text>
        </g>
      ))}

      {/* barra escura: eixo, densidade e faixa de descoberta */}
      {/* De ponta a ponta: a goteira dos nomes das faixas fica por cima da regua,
          e nao ao lado de um rectangulo que comecava a meio do cartao. */}
      <rect className="et-tl__bar" x={-GUTTER} y={barTop} width={width + GUTTER} height={BAR_HEIGHT} rx={16} />
      {scale.gaps.map(([from, to], i) => {
        // Dois chevrons, como numa escala partida de papel: dizem "aqui falta
        // tempo" sem parecerem um tick nem uma divisória de coluna.
        const mid = from + (to - from) / 2;
        const y = barTop + BAR_HEIGHT / 2;
        return (
          <g key={i} className="et-tl__break" data-testid="axis-break">
            <rect x={from} y={barTop} width={to - from} height={BAR_HEIGHT} />
            <path d={`M ${mid - 9} ${y - 9} l 7 9 l -7 9 M ${mid + 2} ${y - 9} l 7 9 l -7 9`} />
          </g>
        );
      })}
      <g className="et-tl__grid" aria-hidden="true">
        {ticks.map((tick) => (
          <line
            key={`g${tick.regionIndex}-${tick.year}`}
            x1={tick.x} y1={barTop + 5} x2={tick.x} y2={barTop + BAR_HEIGHT - 22}
          />
        ))}
      </g>
      <g className="et-tl__density" aria-hidden="true">
        {density.map((b, i) => (
          <rect key={i} x={b.x} y={barTop + 7} width={3} height={Math.min(14, 3 + b.count * 2)} />
        ))}
      </g>
      <g className="et-tl__ticks">
        {ticks.map((tick) => {
          // Um tick no limite do viewBox fica cortado a meio se ficar centrado.
          // Encostar o texto na borda mantém o ano legível sem mentir na posição,
          // porque a marca do tick continua no sítio certo.
          const atStart = tick.x < 34;
          const atEnd = tick.x > width - 34;
          return (
            <text
              key={`${tick.regionIndex}-${tick.year}`}
              x={atStart ? 4 : atEnd ? width - 4 : tick.x}
              y={barTop + BAR_HEIGHT - 11}
              textAnchor={atStart ? 'start' : atEnd ? 'end' : 'middle'}
            >
              {formatSpan({ earliest: tick.year, latest: tick.year }, locale)}
            </text>
          );
        })}
      </g>

      {/* marcas de descoberta dentro da faixa */}
      <g className="et-tl__discovery">
        {resolved.filter((r) => r.discoveryX !== null).map((r) => (
          <path
            key={`d${r.pin.id}`}
            data-discovery={r.pin.id}
            data-selected={r.pin.id === selectedId ? 'true' : undefined}
            className={clsx('et-tl__diamond', dimmed && r.pin.id !== selectedId && 'et-tl__pin--faded')}
            d={`M ${r.discoveryX} ${barTop + 24} l 6 6 l -6 6 l -6 -6 Z`}
          />
        ))}
      </g>
    </svg>
  );
}
